"use client";

import * as React from "react";
import { format } from "date-fns";

import { Button } from "../ui/button";
import { Calendar } from "./calendar";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "../lib/utils";
import { CalendarFold } from "lucide-react";

type DatePickerProps = {
  label?: string;
  name: string;
  placeholder?: string;
  value?: Date | string | undefined;
  onChange?: (date: Date | string | undefined) => void;
  disabled?: boolean;
  className?: string;
};

export function DatePicker({
  label,
  name,
  placeholder = "Select date",
  value,
  onChange,
  disabled = false,
  className,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  const handleDateSelect = (date: Date | undefined) => {
    onChange?.(date?.toISOString());
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-3">
      {label && (
        <Label htmlFor={name} className="px-1">
          {label}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className="hover:bg-white">
          <Button
            variant="unstlyed"
            id={name}
            disabled={disabled}
            className={cn(
              "w-full justify-between font-normal h-12 border-ring ring-ring/50 ring-1 border-none bg-white font-poppins text-xs rounded pl-6",
              !value && "text-[#C1C1C1]",
              className
            )}
          >
            {value ? format(value, "PPP") : placeholder}
            <CalendarFold className="h-4 w-4 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            selected={value ? new Date(value) : undefined}
            onSelect={handleDateSelect}
            captionLayout="dropdown"
            autoFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
